import type { ChecklistDeletionInput } from '@/services/checklist/ChecklistDeletionTransaction';
import type { ChecklistMediaConflictCommit } from '@/services/sync/ChecklistMediaConflictContract';
import type { ChecklistMediaObservationBaselineInput } from '@/services/sync/ChecklistMediaObservationContract';
import type { ChecklistMediaCanonicalAdoptionInput } from '@/services/sync/ChecklistMediaCanonicalAdoptionContract';
import type { HistoricalInsertGuard } from '../services/sync/HistoricalInsertGuard';
import type { ArchivedReplayCommit } from '@/services/sync/ArchivedPartialReplayCoordinator';
/**
 * Interface para adaptadores de banco de dados
 * Implementa operações CRUD genéricas que são comuns a todos os adaptadores
 */
import type { PendingOperationRaw } from '../services/syncService';
import type { PendingWorkOrigin } from '../services/sync/PendingSyncSession';
import type {
  RecoveryCaptureInput,
  RecoveryConsistentSnapshot,
  RecoverySnapshotMediaSource,
  RecoverySnapshotRow,
  RecoverySnapshotTableName,
} from '../types/recovery';
import type { SupportFullStateSnapshot } from '../types/supportFullState';
import type { SupportFullStateMediaSource, SupportFullStateRow } from '../types/supportFullState';
import { hash as naclHash } from 'tweetnacl';
import { isAnswerPatch, hasAnswerObservation } from '@/services/checklist/checklistObservations';
import { parsePartnerStorageReference } from '@/utils/media/partnerStorageReference';

export interface CanonicalMetadataCandidateScope { companyId: string; historicalEnabled: boolean }

const fullStateEncoder = new TextEncoder();

/** Tabelas locais relevantes para uma fotografia completa de suporte. */
export const FULL_STATE_LOCAL_TABLE_NAMES = [
  'pending_operations', 'pending_images', 'pending_deletions', 'sync_conflicts',
  'sync_tombstones', 'sync_cursors', 'accounted_pending_operations', 'deferred_media_cleanup',
  'guincho_empresas', 'guincho_usuarios', 'guincho_templates_checklist',
  'guincho_campos_template', 'guincho_dropdown', 'guincho_marcacao_imagens',
  'guincho_checklists', 'checklist_photos', 'checklist_marcacoes',
  'checklist_signatures', 'checklist_marcacao_bkg_images', 'guincho_valores_checklist',
  'guincho_template_schema_versions', 'guincho_checklist_snapshots',
  'guincho_checklist_snapshot_fields', 'checklist_logs',
] as const;

const fullStateReferences = (value: unknown, target: Set<string>): void => {
  if (typeof value === 'string') {
    if (/^(file|content):\/\//.test(value) || value.startsWith('blob:') || /^(checklist-(photos|signatures|marcacoes|marcacao-bkg))\//.test(value)) target.add(value);
    else if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
      try { fullStateReferences(JSON.parse(value), target); } catch { /* texto livre */ }
    }
  } else if (Array.isArray(value)) value.forEach(item => fullStateReferences(item, target));
  else if (value && typeof value === 'object') Object.values(value as Record<string, unknown>).forEach(item => fullStateReferences(item, target));
};

const hasExplicitCompany = (row: SupportFullStateRow): boolean =>
  typeof row.empresa_id === 'string' && row.empresa_id.length > 0;

const fullStateMime = (uri: string): SupportFullStateMediaSource['contentType'] => {
  const path = uri.split('?')[0].toLowerCase();
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.pdf')) return 'application/pdf';
  return 'image/jpeg';
};

/**
 * O filtro deliberadamente inclui todo dado operacional da empresa e somente
 * filas/evidencias do usuario corrente; pagamentos e outras empresas nunca
 * entram na fotografia de suporte.
 */
export const scopeFullStateSnapshot = (
  raw: Record<string, SupportFullStateRow[]>, companyId: string, userId: string
): SupportFullStateSnapshot => {
  const companyRows = raw.guincho_empresas.filter(row => row.id === companyId);
  const userRows = raw.guincho_usuarios.filter(row => row.empresa_id === companyId);
  const templates = raw.guincho_templates_checklist.filter(row => row.empresa_id === companyId);
  const templateIds = new Set(templates.map(row => String(row.id ?? '')));
  const fields = raw.guincho_campos_template.filter(row => row.empresa_id === companyId);
  const fieldIds = new Set(fields.map(row => String(row.id ?? '')));
  const checklists = raw.guincho_checklists.filter(row => row.empresa_id === companyId);
  const checklistIds = new Set(checklists.map(row => String(row.id ?? '')));
  const values = raw.guincho_valores_checklist.filter(row => row.empresa_id === companyId);
  const ids = new Set([...templateIds, ...fieldIds, ...checklistIds, ...values.map(row => String(row.id ?? ''))]);
  const isScopedItem = (row: Record<string, unknown>) => ids.has(String(row.item_id ?? row.itemId ?? row.checklist_id ?? row.template_id ?? row.campo_id ?? ''));
  const hasScopeToken = (value: unknown) => {
    const text = String(value ?? '');
    return text === companyId || text === userId || text.startsWith(`${companyId}/`) || text.startsWith(`${userId}/`) ||
      text.includes(`/${companyId}/`) || text.includes(`/${userId}/`);
  };
  const retainedReferences = new Set<string>();
  ['accounted_pending_operations', 'sync_conflicts', 'deferred_media_cleanup'].forEach(name => (raw[name] ?? []).filter(row =>
    row.empresa_id === companyId && (!row.user_id || row.user_id === userId)).forEach(row =>
      fullStateReferences(row, retainedReferences)));
  const localRows = (name: string) => raw[name].filter(row => {
    if (typeof row.id === 'string' && row.id.startsWith('checklist-photos/retained-conflicts/')) {
      return row.id.split('/')[2] === companyId &&
        (row.id.split('/')[3] === userId || retainedReferences.has(row.id));
    }
    if (hasExplicitCompany(row)) return row.empresa_id === companyId;
    return isScopedItem(row) || hasScopeToken(row.path) || hasScopeToken(row.id);
  });
  const pendingOperationMatches = (row: SupportFullStateRow) => {
    if (hasExplicitCompany(row)) return row.empresa_id === companyId;
    if (typeof row.data === 'string') {
      try {
        const payload = JSON.parse(row.data) as Record<string, unknown>;
        if (payload.empresa_id) return payload.empresa_id === companyId && (!payload.usuario_id || payload.usuario_id === userId);
      } catch { /* payload legado nao estruturado */ }
    }
    return isScopedItem(row) || hasScopeToken(row.path) || hasScopeToken(row.id);
  };
  const snapshots = raw.guincho_checklist_snapshots.filter(row => row.empresa_id === companyId);
  const snapshotIds = new Set(snapshots.map(row => String(row.id ?? '')));
  const tables: Record<string, SupportFullStateRow[]> = {
    guincho_empresas: companyRows, guincho_usuarios: userRows,
    guincho_templates_checklist: templates, guincho_campos_template: fields,
    guincho_dropdown: raw.guincho_dropdown.filter(row => !row.empresa_id || row.empresa_id === companyId),
    guincho_marcacao_imagens: raw.guincho_marcacao_imagens.filter(row => row.empresa_id === companyId),
    guincho_checklists: checklists, guincho_valores_checklist: values,
    guincho_template_schema_versions: raw.guincho_template_schema_versions.filter(row => row.empresa_id === companyId),
    guincho_checklist_snapshots: snapshots,
    guincho_checklist_snapshot_fields: raw.guincho_checklist_snapshot_fields.filter(row => hasExplicitCompany(row)
      ? row.empresa_id === companyId
      : snapshotIds.has(String(row.snapshot_id ?? '')) || checklistIds.has(String(row.checklist_id ?? '')) || fieldIds.has(String(row.campo_id ?? row.field_id ?? ''))),
    checklist_logs: raw.checklist_logs.filter(row => hasExplicitCompany(row)
      ? row.empresa_id === companyId
      : checklistIds.has(String(row.checklist_id ?? '')) || hasScopeToken(row.path) || hasScopeToken(row.id)),
    checklist_photos: localRows('checklist_photos'), checklist_marcacoes: localRows('checklist_marcacoes'),
    checklist_signatures: localRows('checklist_signatures'), checklist_marcacao_bkg_images: localRows('checklist_marcacao_bkg_images'),
    pending_operations: raw.pending_operations.filter(pendingOperationMatches),
    pending_images: localRows('pending_images'), pending_deletions: localRows('pending_deletions'),
    sync_conflicts: raw.sync_conflicts.filter(row => row.empresa_id === companyId && (!row.user_id || row.user_id === userId)),
    sync_tombstones: raw.sync_tombstones.filter(row => row.empresa_id === companyId),
    sync_cursors: raw.sync_cursors.filter(row => row.empresa_id === companyId),
    accounted_pending_operations: raw.accounted_pending_operations.filter(row => row.empresa_id === companyId && (!row.user_id || row.user_id === userId)),
    deferred_media_cleanup: (raw.deferred_media_cleanup ?? []).filter(row => row.empresa_id === companyId && row.user_id === userId),
  };
  const references = new Map<string, { sourceUri: string; table: string; rowId: string | null; checklistId: string | null; fieldId: string | null; path: string | null }>();
  Object.entries(tables).forEach(([table, rows]) => rows.forEach(row => {
    const rowReferences = new Set<string>();
    fullStateReferences(row, rowReferences);
    rowReferences.forEach(sourceUri => {
      const identity = `${table}:${String(row.id ?? '')}:${sourceUri}`;
      references.set(identity, {
        sourceUri,
        table, rowId: typeof row.id === 'string' ? row.id : null,
        checklistId: typeof row.checklist_id === 'string' ? row.checklist_id : null,
        fieldId: typeof row.campo_id === 'string' ? row.campo_id : typeof row.field_id === 'string' ? row.field_id : null,
        path: typeof row.path === 'string' ? row.path : null,
      });
    });
  }));
  const media = [...references.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([identity, origin]) => {
    const { sourceUri, ...sourceOrigin } = origin;
    return {
      mediaId: `media-${Array.from(new Uint8Array(naclHash(fullStateEncoder.encode(identity)))).map(value => value.toString(16).padStart(2, '0')).join('').slice(0, 32)}`,
      sourceUri, contentType: fullStateMime(sourceUri), checklistId: origin.checklistId, fieldId: origin.fieldId,
      origin: sourceOrigin,
    };
  });
  return { tables, media };
};

export const RECOVERY_LOCAL_TABLE_NAMES: Record<RecoverySnapshotTableName, string> = {
  pending_operations: 'pending_operations',
  pending_deletions: 'pending_deletions',
  templates: 'guincho_templates_checklist',
  template_fields: 'guincho_campos_template',
  dropdown_options: 'guincho_dropdown',
  checklists: 'guincho_checklists',
  checklist_values: 'guincho_valores_checklist',
  sync_conflicts: 'sync_conflicts',
};

type RecoveryRawTables = Record<RecoverySnapshotTableName, RecoverySnapshotRow[]>;

/**
 * Entrada imutavel para a criacao local de um checklist com seu snapshot.
 * Os quatro conjuntos devem ser persistidos na mesma transacao ou nenhum
 * deles pode ficar visivel para o sync.
 */
export interface ChecklistSnapshotAtomicCreateInput {
  checklist: { collection: string; record: Record<string, unknown> };
  /** Registro provisório do catálogo, quando a versão canônica ainda não foi puxada. */
  schemaVersion?: { collection: string; record: Record<string, unknown> };
  snapshot: { collection: string; record: Record<string, unknown> };
  snapshotFields: { collection: string; records: Record<string, unknown>[] };
  pendingOperation: {
    id: string;
    collection: string;
    itemId: string;
    data: string;
    timestamp: number;
  };
}

export interface ChecklistSnapshotCanonicalReconciliationInput {
  provisionalSchemaVersionId: string | null;
  schemaVersion: { collection: string; record: Record<string, unknown> };
  /** Payload da operação create usado para preservar uma edição local concorrente. */
  expectedChecklist?: Record<string, unknown>;
  checklist: { collection: string; record: Record<string, unknown> };
  snapshot: { collection: string; record: Record<string, unknown> };
  snapshotFields: { collection: string; records: Record<string, unknown>[] };
}

export type ChecklistStructuralFieldMutation =
  | {
      operation: 'create';
      record: Record<string, unknown>;
    }
  | {
      operation: 'update';
      id: string;
      updates: Record<string, unknown>;
    }
  | {
      operation: 'delete';
      id: string;
      updates: Record<string, unknown>;
      pendingData: Record<string, unknown>;
    };

export interface RetainedChecklistUploadInput {
  pending: Record<string, any>;
  companyId: string;
  canCommit: () => boolean;
}

export interface StructuralMutationGuard {
  origin: { userId: string; empresaId: string };
  canCommit: () => boolean;
}

export interface ChecklistStructuralMutationInput {
  guard?: StructuralMutationGuard;
  templateId: string;
  fieldMutations: ChecklistStructuralFieldMutation[];
}

export interface ChecklistStructuralMutationResult {
  templateVersion: number;
  fieldResults: boolean[];
}

const valueMatches = (value: unknown, expected: string): boolean =>
  typeof value === 'string' && value === expected;

const parsePendingPayload = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string' || value.length === 0) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object'
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

const cloneAndFreeze = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return Object.freeze(value.map(item => cloneAndFreeze(item)));
  }
  if (value && typeof value === 'object') {
    return Object.freeze(Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => [key, cloneAndFreeze(item)])
    ));
  }
  return value;
};

const cloneAndFreezeRows = (
  rows: readonly RecoverySnapshotRow[]
): readonly RecoverySnapshotRow[] => rows.map(row =>
  cloneAndFreeze(row) as RecoverySnapshotRow
);

const isLocalMediaReference = (value: string): boolean =>
  value.startsWith('file://') ||
  value.startsWith('content://') ||
  value.startsWith('blob:') ||
  value.startsWith('checklist-photos/') ||
  value.startsWith('checklist-signatures/') ||
  value.startsWith('checklist-marcacoes/') ||
  value.startsWith('checklist-marcacao-bkg/');

const collectLocalReferences = (value: unknown, target: Set<string>): void => {
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (isLocalMediaReference(normalized)) {
      target.add(normalized);
      return;
    }
    if (
      (normalized.startsWith('{') && normalized.endsWith('}')) ||
      (normalized.startsWith('[') && normalized.endsWith(']'))
    ) {
      try {
        collectLocalReferences(JSON.parse(normalized), target);
      } catch {
        // Texto comum de checklist; nao e referencia local estruturada.
      }
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectLocalReferences(item, target));
    return;
  }
  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>)
      .forEach(item => collectLocalReferences(item, target));
  }
};

const contentTypeForLocalReference = (uri: string): string => {
  const normalized = uri.split('?')[0].toLowerCase();
  if (normalized.endsWith('.png')) return 'image/png';
  if (normalized.endsWith('.webp')) return 'image/webp';
  if (normalized.endsWith('.heic') || normalized.endsWith('.heif')) return 'image/heic';
  if (normalized.endsWith('.pdf')) return 'application/pdf';
  return 'image/jpeg';
};

/**
 * Fecha o escopo do snapshot no tenant autenticado. Checklists e estruturas de
 * template sao compartilhados dentro da empresa; filas com user_id ficam
 * restritas ao usuario atual.
 */
export const scopeRecoverySnapshot = (
  raw: RecoveryRawTables,
  input: RecoveryCaptureInput
): RecoveryConsistentSnapshot => {
  const { companyId, userId } = input.identity;
  const templates = raw.templates.filter(row => valueMatches(row.empresa_id, companyId));
  const templateIds = new Set(templates.map(row => String(row.id ?? '')));
  const templateFields = raw.template_fields.filter(row =>
    valueMatches(row.empresa_id, companyId) && templateIds.has(String(row.template_id ?? ''))
  );
  const fieldIds = new Set(templateFields.map(row => String(row.id ?? '')));
  const checklists = raw.checklists.filter(row => valueMatches(row.empresa_id, companyId));
  const checklistIds = new Set(checklists.map(row => String(row.id ?? '')));
  const checklistValues = raw.checklist_values.filter(row =>
    valueMatches(row.empresa_id, companyId) && checklistIds.has(String(row.checklist_id ?? ''))
  );
  const valueIds = new Set(checklistValues.map(row => String(row.id ?? '')));
  const scopedItemIds = new Set([...templateIds, ...fieldIds, ...checklistIds, ...valueIds]);

  const pendingOperations = raw.pending_operations.filter(row => {
    if (row.user_id && !valueMatches(row.user_id, userId)) return false;
    if (scopedItemIds.has(String(row.item_id ?? ''))) return true;
    const payload = parsePendingPayload(row.data);
    return Boolean(
      payload &&
      valueMatches(payload.empresa_id, companyId) &&
      (!payload.usuario_id || valueMatches(payload.usuario_id, userId))
    );
  });
  const pendingDeletions = raw.pending_deletions.filter(row => {
    const itemId = String(row.itemId ?? row.item_id ?? '');
    const path = String(row.path ?? '');
    return scopedItemIds.has(itemId) ||
      path.startsWith(`${companyId}/`) ||
      path.includes(`/${companyId}/`);
  });
  const syncConflicts = raw.sync_conflicts.filter(row =>
    valueMatches(row.empresa_id, companyId) &&
    (!row.user_id || valueMatches(row.user_id, userId))
  );

  const tables: RecoveryRawTables = {
    pending_operations: [...pendingOperations],
    pending_deletions: [...pendingDeletions],
    templates: [...templates],
    template_fields: [...templateFields],
    // Dropdown e referencia global, sem tenant no schema local.
    dropdown_options: [...raw.dropdown_options],
    checklists: [...checklists],
    checklist_values: [...checklistValues],
    sync_conflicts: [...syncConflicts],
  };

  const references = new Set<string>();
  Object.values(tables).forEach(rows => rows.forEach(row => collectLocalReferences(row, references)));
  for (const reference of references) {
    const relativePrefix = [
      'checklist-photos/', 'checklist-signatures/',
      'checklist-marcacoes/', 'checklist-marcacao-bkg/',
    ].find(prefix => reference.startsWith(prefix));
    if (relativePrefix && !reference.startsWith(`${relativePrefix}${companyId}/`)) {
      throw new Error('Referencia local fora do escopo da empresa no snapshot recovery.');
    }
  }
  const media: RecoverySnapshotMediaSource[] = [...references]
    .sort()
    .map((sourceUri, index) => {
      const sourceValue = checklistValues.find(row => {
        const rowReferences = new Set<string>();
        collectLocalReferences(row, rowReferences);
        return rowReferences.has(sourceUri);
      });
      return {
        mediaId: `media-${String(index + 1).padStart(6, '0')}`,
        checklistId: typeof sourceValue?.checklist_id === 'string'
          ? sourceValue.checklist_id : null,
        fieldId: typeof sourceValue?.campo_id === 'string'
          ? sourceValue.campo_id : null,
        sourceUri,
        contentType: contentTypeForLocalReference(sourceUri),
      };
    });

  return Object.freeze({
    tables: Object.fromEntries(
      Object.entries(tables).map(([name, rows]) => [name, cloneAndFreezeRows(rows)])
    ) as Record<RecoverySnapshotTableName, readonly RecoverySnapshotRow[]>,
    media: Object.freeze(media.map(item => Object.freeze(item))),
  });
};

/**
 * Define a mutacao local ou a aplicacao de um payload que ja foi confirmado
 * remotamente. O objeto nomeado evita que `true` seja interpretado de forma
 * ambigua em novos pontos de escrita.
 */
export interface DatabaseMutationOptions {
  skipPendingOperation?: boolean;
  source?: 'local' | 'remote';
  expectedPartnerRevision?: number;
}

export type DatabaseMutationInput = boolean | DatabaseMutationOptions;

export const resolveDatabaseMutationOptions = (
  input: DatabaseMutationInput = false
): Required<Pick<DatabaseMutationOptions, 'skipPendingOperation' | 'source'>> & Pick<DatabaseMutationOptions, 'expectedPartnerRevision'> => {
  if (typeof input === 'boolean') {
    return {
      skipPendingOperation: input,
      // Mantem a semantica legada dos chamadores booleanos. Novas aplicacoes
      // remotas devem usar o objeto nomeado com source='remote'.
      source: 'local',
    };
  }

  const skipPendingOperation = input.skipPendingOperation === true;
  const source = input.source ?? (skipPendingOperation ? 'remote' : 'local');

  // Uma aplicação remota confirmada precisa ser explicitamente sem outbox.
  // Mantemos combinações locais válidas (inclusive local + skip) para não
  // quebrar os chamadores legados, mas recusamos a combinação incoerente.
  if (source === 'remote' && !skipPendingOperation) {
    throw new Error(
      'Mutação remota exige skipPendingOperation=true; use source=local para uma escrita local.'
    );
  }

  return {
    skipPendingOperation,
    source,
    ...(input.expectedPartnerRevision !== undefined ? { expectedPartnerRevision: input.expectedPartnerRevision } : {}),
  };
};

/** Referência e fila de uma mídia são confirmadas na mesma transação local. */
export interface ChecklistMediaPendingInput {
  cleanupId?: string;
  logicalChanges?: string;
  clearReference?: boolean;
  expectedValue?: string;
  pendingOrigin: PendingWorkOrigin | null;
  canAcknowledge?: () => boolean;
  onStage?: (stage: 'prepare_reading' | 'prepare_transaction' | 'prepare_value' | 'prepare_pending' | 'prepare_cleanup' | 'prepare_operation' | 'prepare_commit') => void;
  checklistId: string;
  campoId: string;
  pending: {
    id: string;
    item_type: 'CHECKLIST';
    item_id: string;
    campo_id: string;
    local_uri: string;
    bucket: string;
    path: string;
    column_name: null;
    user_id: string | null;
    empresa_id: string | null;
    baseline_captured?: number | null;
    expected_partner_revision?: number | null;
    expected_remote_value?: string | null;
    expected_remote_updated_at?: string | null;
    prepared_public_url?: string | null;
    created_at: number;
  } | null;
}

/** Preserva a versão remota anterior antes de trocar o valor pelo URI local. */
export function captureChecklistMediaBaseline(
  existing: { valor?: unknown; updated_at?: unknown } | null | undefined,
  previous: { local_uri?: unknown; prepared_public_url?: unknown; baseline_captured?: unknown; expected_remote_value?: unknown;
    expected_remote_updated_at?: unknown } | null | undefined
): { baseline_captured: number; expected_remote_value: string | null; expected_remote_updated_at: string | null } {
  if (previous && (previous.local_uri === existing?.valor || previous.prepared_public_url === existing?.valor) &&
      previous.baseline_captured === 1) {
    return {
      baseline_captured: 1,
      expected_remote_value: typeof previous.expected_remote_value === 'string' ? previous.expected_remote_value : null,
      expected_remote_updated_at: typeof previous.expected_remote_updated_at === 'string'
        ? previous.expected_remote_updated_at : null,
    };
  }
  if (!existing) {
    return { baseline_captured: 1, expected_remote_value: null, expected_remote_updated_at: null };
  }
  if (typeof existing.valor !== 'string' && existing.valor !== null) {
    return { baseline_captured: 0, expected_remote_value: null, expected_remote_updated_at: null };
  }
  const value = existing.valor;
  if (value !== null && value !== '' && !/^https?:\/\//.test(value) && !parsePartnerStorageReference(value)) {
    return { baseline_captured: 1, expected_remote_value: null, expected_remote_updated_at: null };
  }
  const updatedAt = existing?.updated_at;
  if (typeof updatedAt === 'string' && Number.isFinite(Date.parse(updatedAt))) {
    return { baseline_captured: 1, expected_remote_value: value, expected_remote_updated_at: updatedAt };
  }
  return { baseline_captured: 0, expected_remote_value: null, expected_remote_updated_at: null };
}

/** Identidade original da fila, que pode estar incompleta em dados legados. */
export interface PendingQueueOrigin {
  userId: string | null;
  empresaId: string | null;
}

export function assertChecklistMediaPendingInput(
  collection: string,
  rows: Record<string, any>[],
  media: ChecklistMediaPendingInput
): void {
  if (collection !== 'guincho_valores_checklist' || rows.length !== 1 ||
      !rows[0].empresa_id || !rows[0].id ||
      rows[0].checklist_id !== media.checklistId ||
      (rows[0].snapshot_field_id || rows[0].campo_id) !== media.campoId ||
      (media.pending && (!media.pending.id || media.pending.item_type !== 'CHECKLIST' ||
        media.pending.item_id !== media.checklistId || media.pending.campo_id !== media.campoId ||
        media.pending.local_uri !== rows[0].valor))) {
    throw new Error('Identidade inválida na persistência de mídia do checklist.');
  }
}

export interface ChecklistMediaCompletionInput {
  empresaId: string;
  pendingEmpresaId: string | null;
  checklistId: string;
  campoId: string;
  pendingId: string;
  localUri: string;
  publicUrl: string;
  userId: string | null;
  canAcknowledge?: () => boolean;
}

export interface ChecklistMediaFinalizationInput {
  pending: Record<string, any>;
  value: Record<string, any>;
  publicUrl: string;
  remoteUpdatedAt: string;
  remoteObservation?: string | null;
  remoteSnapshotFieldId?: string | null;
  remoteCampoId?: string | null;
  /** Nota local mais nova que deve continuar exatamente na parcial após o ACK da imagem. */
  expectedObservation?: string | null;
  recoveryReceipt?: Record<string, any>;
  canAcknowledge: () => boolean;
}

/** Remove somente o campo de mídia já confirmado, sem baixar outras respostas da parcial. */
export function planAcknowledgedMediaPartial(
  rows: ReadonlyArray<Record<string, any>>,
  pending: Record<string, any>,
  publicUrl: string,
  expectedObservation?: string | null
): { id: string; remainingData: string | null } | null {
  if (typeof pending.campo_id !== 'string' || !pending.campo_id) {
    throw new Error('Campo ausente para confirmar parcial de mídia.');
  }
  const exact: Array<{ id: string; remainingData: string | null }> = [];
  for (const row of rows) {
    if (row.operation !== 'checklist_partial_update' || row.collection !== 'guincho_valores_checklist' ||
        row.item_id !== pending.item_id || (row.user_id ?? null) !== (pending.user_id ?? null) ||
        (row.empresa_id ?? null) !== (pending.empresa_id ?? null)) continue;
    let answers: Record<string, unknown>;
    try {
      answers = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
    } catch { throw new Error('Parcial de mídia inválida durante ACK; fila preservada.'); }
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
      throw new Error('Parcial de mídia inválida durante ACK; fila preservada.');
    }
    if (!Object.prototype.hasOwnProperty.call(answers, pending.campo_id)) continue;
    const answer = answers[pending.campo_id];
    if (answer === publicUrl) {
      if (expectedObservation !== undefined) {
        throw new Error('Parcial não preserva a observação durante o ACK; fila preservada.');
      }
      const remaining = { ...answers };
      delete remaining[pending.campo_id];
      exact.push({ id: row.id, remainingData: Object.keys(remaining).length ? JSON.stringify(remaining) : null });
      continue;
    }
    const noteOnly = hasAnswerObservation(answer) && !Object.prototype.hasOwnProperty.call(answer, 'valor');
    if (!isAnswerPatch(answer) || (!noteOnly && answer.valor !== publicUrl)) {
      throw new Error('Parcial de mídia mudou antes do ACK; fila preservada.');
    }
    if (expectedObservation !== undefined && (!hasAnswerObservation(answer) || answer.observacao !== expectedObservation)) {
      throw new Error('Observação mudou antes do ACK; fila preservada.');
    }
    // Confirmar a imagem não confirma a observação; a parcial segue para o V2/V3.
    exact.push({ id: row.id, remainingData: JSON.stringify(answers) });
  }
  if (exact.length > 1) throw new Error('Parciais de mídia ambíguas durante ACK; fila preservada.');
  if (expectedObservation !== undefined && exact.length === 0) {
    throw new Error('Observação mais nova ausente da parcial durante o ACK; fila preservada.');
  }
  return exact[0] ?? null;
}

export interface IDatabaseAdapter {
  commitArchivedPartialReplay(input: ArchivedReplayCommit): Promise<boolean>;
  advanceChecklistMediaObservationBaseline(input: ChecklistMediaObservationBaselineInput): Promise<boolean>;
  adoptChecklistMediaNoteCanonical(input: ChecklistMediaCanonicalAdoptionInput): Promise<boolean>;
  commitChecklistMediaConflict(input: ChecklistMediaConflictCommit): Promise<boolean>;
  finalizeChecklistMedia(input: ChecklistMediaFinalizationInput): Promise<boolean>;
  completeChecklistMedia(input: ChecklistMediaCompletionInput): Promise<boolean>;

  deleteChecklistPreservingMedia(input: ChecklistDeletionInput): Promise<boolean>;

  retainTombstonedChecklistUpload(input: RetainedChecklistUploadInput): Promise<boolean>;
  clearTablesIfUnchanged(expected: Record<string, Record<string, any>[]>, preserve: readonly string[], canClear?: () => boolean): Promise<boolean>;

  persistChecklistMediaPending(
    collection: string,
    rows: Record<string, any>[],
    media: ChecklistMediaPendingInput
  ): Promise<void>;

  /**
   * Inicializa o banco de dados
   * 
   * @param userId ID do usuário atual (opcional)
   * @returns Promise que resolve quando a inicialização for concluída
   */
  initialize(userId?: string): Promise<void>;

  /**
   * Atualiza o ID do usuário no adaptador — útil para setar userId após o login
   * 
   * @param userId ID do usuário logado
   */
  setUserId(userId: string): void;

  /**
   * Cria um novo item na coleção especificada
   * 
   * @param collection Nome da coleção/tabela
   * @param item Objeto a ser criado
   * @param options Define a origem da mutação e se a operação deve ir para a outbox (opcional)
   * @returns Promise com o ID do item criado
   */
  createItem<T>(collection: string, item: T, options?: DatabaseMutationInput): Promise<string>;

  /**
   * Lê um item específico de uma coleção
   * 
   * @param collection Nome da coleção/tabela
   * @param id ID do item a ser lido
   * @returns Promise com o item encontrado ou null se não encontrado
   */
  readItem<T>(collection: string, id: string): Promise<T | null>;

  /**
   * Atualiza um item existente em uma coleção
   * 
   * @param collection Nome da coleção/tabela
   * @param id ID do item a ser atualizado
   * @param updates Objeto com atualizações parciais
   * @param options Define a origem da mutação e se a operação deve ir para a outbox (opcional)
   * @returns Promise com boolean indicando sucesso
   */
  updateItem<T>(collection: string, id: string, updates: Partial<T>, options?: DatabaseMutationInput): Promise<boolean>;

  /** Atualiza um item somente se a linha atual ainda corresponde ao payload esperado. */
  updateItemIfUnchanged(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    expected: Record<string, unknown>,
    skipPendingOperation?: boolean,
    canAcknowledge?: () => boolean,
    guard?: HistoricalInsertGuard
  ): Promise<boolean>;

  /** Atualiza aposentadoria e pendencia `delete` no mesmo commit local. */
  retireItemAndPending(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    pendingData: Record<string, unknown>,
    guard?: StructuralMutationGuard
  ): Promise<boolean>;

  /** Atualiza versao, campos e fila estrutural em uma unica transacao local. */
  applyChecklistStructuralMutation(
    input: ChecklistStructuralMutationInput
  ): Promise<ChecklistStructuralMutationResult>;

  /**
   * Remove um item de uma coleção
   * 
   * @param collection Nome da coleção/tabela
   * @param id ID do item a ser removido
   * @param options Define a origem da mutação e se a operação deve ir para a outbox (opcional)
   * @returns Promise com boolean indicando sucesso
   */
  deleteItem(collection: string, id: string, options?: DatabaseMutationInput): Promise<boolean>;

  /** Remove um item somente se a linha atual ainda corresponde ao payload esperado. */
  deleteItemIfUnchanged(
    collection: string,
    id: string,
    expected: Record<string, unknown>,
    skipPendingOperation?: boolean,
    canAcknowledge?: () => boolean
  ): Promise<boolean>;

  /**
   * Lista itens de uma coleção, opcionalmente com filtros
   * 
   * @param collection Nome da coleção/tabela
   * @param query Objeto de consulta para filtrar os resultados
   * @returns Promise com array de itens encontrados
   */
  listItemsByIdPage<T>(collection: string, afterId: string, limit: number, metadata?: CanonicalMetadataCandidateScope): Promise<T[]>;

  listItems<T>(collection: string, query?: any): Promise<T[]>;

  /**
   * Registra uma operação pendente para sincronização posterior
   * 
   * @param operation Tipo de operação ('create', 'update', 'delete', 'checklist_partial_update')
   * @param collection Nome da coleção/tabela
   * @param id ID do item afetado
   * @param data Dados relacionados à operação (opcional)
   * @returns Promise com o ID da operação pendente
   */
  registerPendingOperation(
    operation: 'create' | 'update' | 'delete' | 'checklist_partial_update',
    collection: string,
    id: string,
    data?: any,
    origin?: PendingWorkOrigin | null
  ): Promise<string>;

  /**
   * Busca operações pendentes para sincronização
   * 
   * @returns Promise com array de operações pendentes
   */
  getPendingOperations(): Promise<PendingOperationRaw[]>;

  /**
   * Remove uma operação pendente após sincronização bem-sucedida
   * 
   * @param id ID da operação pendente
   * @returns Promise com boolean indicando sucesso
   */
  removePendingOperation(id: string): Promise<boolean>;

  /** Remove uma pendencia somente se o registro ainda corresponde ao snapshot esperado. */
  removePendingOperationIfUnchanged(
    id: string,
    expected: Pick<PendingOperationRaw, 'operation' | 'collection' | 'item_id' | 'data' | 'timestamp' | 'user_id' | 'empresa_id'>,
    canAcknowledge?: () => boolean
  ): Promise<boolean>;

  /**
   * Fecha a conexão com o banco de dados
   * 
   * @returns Promise que resolve quando a conexão for fechada
   */
  close(): Promise<void>;

  /**
   * Realiza upsert em lote de múltiplos registros na coleção/tabela
   * @param collection Nome da coleção/tabela
   * @param rows Array de objetos a serem upsertados
   * @param skipPendingOperation Se true, não registra operações pendentes
   */
  bulkUpsert(collection: string, rows: any[], skipPendingOperation?: boolean): Promise<void>;

  /** Insere em lote somente registros ainda ausentes, sem sobrescrever estado local. */
  bulkInsertIfAbsent(collection: string, rows: any[], guard?: HistoricalInsertGuard): Promise<number>;

  /**
   * Cria checklist, snapshot, campos e a pendencia create em uma unica
   * transacao local. Repetir a mesma entrada e idempotente; qualquer conflito
   * de conteudo falha fechado.
   */
  createChecklistSnapshotAndPending(input: ChecklistSnapshotAtomicCreateInput): Promise<void>;

  /** Reconcilia o agregado local com a resposta canônica sem tocar na fila ou em conflitos. */
  reconcileChecklistSnapshotCanonical?(
    input: ChecklistSnapshotCanonicalReconciliationInput
  ): Promise<void>;

  /** Captura tabelas e referencias de midia em uma unica transacao consistente. */
  captureRecoverySnapshot(input: RecoveryCaptureInput): Promise<RecoveryConsistentSnapshot>;

  /** Fotografia transacional de suporte, sempre fechada em empresa/usuario. */
  captureFullStateSnapshot(companyId: string, userId: string): Promise<SupportFullStateSnapshot>;
}

export default IDatabaseAdapter;
